// src/pages/Documents.jsx
import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

const SECTIONS = [
  { key: "howitswork", label: "Comment ça marche", icon: "info" },
  { key: "mentions", label: "Mentions légales", icon: "document" },
  { key: "cgv", label: "CGV", icon: "clipboard" },
];

export default function Documents() {
  const [data, setData] = useState({ howitswork: "", mentions: "", cgv: "" });
  const [activeTab, setActiveTab] = useState("howitswork");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    async function fetchAll() {
      setIsLoading(true);
      const docRef = doc(db, "CGV", "CGV");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data());
      }
      setIsLoading(false);
    }
    fetchAll();
  }, []);

  const handleChange = (value) => {
    setData((prev) => ({ ...prev, [activeTab]: value }));
    setHasChanges(true);
  };

  const update = async () => {
    setSaving(true);
    const docRef = doc(db, "CGV", "CGV");
    try {
      await updateDoc(docRef, { [activeTab]: data[activeTab] });
      
      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Modification effectuée avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
      
      setHasChanges(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const getIcon = (iconType) => {
    const icons = {
      info: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
          />
        </svg>
      ),
      document: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
          />
        </svg>
      ),
      clipboard: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
          />
        </svg>
      ),
    };
    return icons[iconType] || icons.document;
  };

  const wordCount = data[activeTab]?.trim().split(/\s+/).length || 0;
  const charCount = data[activeTab]?.length || 0;

  return (
    <PageTemplate
      loading={isLoading}
      content={
        <div className="space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-blue-500 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des contenus juridiques
                </h3>
                <p className="text-sm text-gray-600">
                  Modifiez les documents légaux de votre application. Les
                  changements seront visibles immédiatement pour les utilisateurs.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SECTIONS.map(({ key, label, icon }) => (
              <Card
                key={key}
                className={`cursor-pointer transition-all ${
                  activeTab === key
                    ? "ring-2 ring-[#2086b4] shadow-md"
                    : "hover:shadow-md"
                }`}
                onClick={() => setActiveTab(key)}
              >
                <CardContent className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      {label}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {data[key]?.length || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">caractères</p>
                  </div>
                  <div
                    className={`p-3 rounded-full ${
                      activeTab === key
                        ? "bg-[#2086b4] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {getIcon(icon)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Editor Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {getIcon(
                      SECTIONS.find((s) => s.key === activeTab)?.icon || "document"
                    )}
                  </div>
                  <div>
                    <CardTitle>
                      {SECTIONS.find((s) => s.key === activeTab)?.label}
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {wordCount} mot{wordCount > 1 ? "s" : ""} · {charCount}{" "}
                      caractère{charCount > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                {hasChanges && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <span className="w-2 h-2 mr-2 bg-orange-400 rounded-full animate-pulse"></span>
                    Non sauvegardé
                  </span>
                )}
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor={activeTab}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Contenu du document
                  </label>
                  <textarea
                    id={activeTab}
                    rows={15}
                    className="w-full rounded-lg border border-gray-300 p-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#2086b4] focus:border-transparent transition-all font-mono text-sm"
                    value={data[activeTab] || ""}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="Saisissez le contenu du document ici..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                      />
                    </svg>
                    <span>
                      Les modifications seront immédiatement visibles dans
                      l'application
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setData((prev) => ({ ...prev, [activeTab]: "" }));
                        setHasChanges(true);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Effacer
                    </button>
                    <button
                      onClick={update}
                      disabled={saving || !hasChanges}
                      className="px-6 py-2 bg-[#2086b4] text-white rounded-lg font-medium hover:bg-[#1a6b94] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                            />
                          </svg>
                          Enregistrer les modifications
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}